import Guid from "_shared/guids/guid";
import BookmarkToggle from "marketplace/bookmarks/_views/bookmark-toggle";
import {
  useCreateTeamBookmarkMutation,
  useDeleteTeamBookmarkMutation,
} from "marketplace/bookmarks/_api/bookmark-api";
import useTeamBookmarkStatus from "marketplace/bookmarks/_hooks/use-team-bookmark-status";
import { useSnackbar } from "_shared/_providers/snackbars/snackbars";

export interface TeamBookmarkButtonProps {
  className?: string;
  teamId?: Guid;
  /** The result's name, which the toggle's accessible name repeats. */
  name: string;
}

export default function TeamBookmarkButton(props: Readonly<TeamBookmarkButtonProps>) {
  const { className, teamId, name } = props;

  const { enqueueSnackbar } = useSnackbar();

  // One team's bookmark, not the whole list: the hook puts the ids that every card on a results
  // page asks for in the same tick into one bulk request. Undefined while it loads, null for none.
  const { bookmark: existingBookmark, hasFailed, retry } = useTeamBookmarkStatus(teamId);
  const [createTeamBookmark, { isLoading: isCreating }] = useCreateTeamBookmarkMutation();
  const [deleteTeamBookmark, { isLoading: isDeleting }] = useDeleteTeamBookmarkMutation();

  const isLoading = (existingBookmark === undefined && !hasFailed) || isCreating || isDeleting;

  async function handleBookmarkClick() {
    // Whether the team is bookmarked is unknown, so neither adding nor removing is safe. Ask again.
    if (hasFailed) {
      retry();
      return;
    }
    try {
      if (existingBookmark) {
        await deleteTeamBookmark({
          bookmarkId: existingBookmark.id,
          teamId: existingBookmark.teamId,
        }).unwrap();
        enqueueSnackbar("Removed bookmark", { variant: "info" });
      } else {
        if (!teamId) throw new Error("Bookmark Team: Team ID is undefined");
        await createTeamBookmark({ teamId: teamId.value }).unwrap();
        enqueueSnackbar("Added bookmark", { variant: "info" });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't save bookmark", { variant: "error" });
    }
  }

  return (
    <BookmarkToggle
      className={className}
      name={name}
      bookmarked={Boolean(existingBookmark)}
      disabled={!teamId || isLoading}
      onToggle={handleBookmarkClick}
    />
  );
}
